import { useState, useMemo, useCallback } from 'react';
import PageTitle from '../../components/PageTitle';
import { useQueryClient } from '@tanstack/react-query';
import { useContextList } from '../../hooks/useContext';
import { useUnstructuredContextList } from '../../hooks/useUnstructuredContext';
import { useDimensionsList } from '../../hooks/useDimension';
import { contextService, type ContextItem } from '../../services/context.service';
import { unstructuredContextService, type UnstructuredContextItem } from '../../services/unstructuredContext.service';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { DimensionFilterTable } from '../../components/DimensionFilterTable';
import { Loader2, Lightbulb, FileText, Plus, Search, Settings, Trash2, Filter, AlertTriangle } from 'lucide-react';
import { ContextViewToggle, type ContextViewMode } from '../../components/ViewModeToggle';
import type { AnalysisDimensionFilter } from '../../utils/filterComparators';
import type { ComboboxOption } from '../../components/ui/combobox';

// ─── Analytics Context (structured) ─────────────────────────────────────────

function AnalyticsContextView() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 50;

  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ContextItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editFilters, setEditFilters] = useState<AnalysisDimensionFilter[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ContextItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, isLoading, error } = useContextList({ page, pageSize });

  const { data: dimensionsData } = useDimensionsList({ page: 1, pageSize: 1000 });

  const dimensionOptions: ComboboxOption[] = useMemo(() => {
    if (!dimensionsData?.records) return [];
    return dimensionsData.records.map((dim) => ({ value: dim.id, label: dim.name }));
  }, [dimensionsData?.records]);

  const contextItems = useMemo(() => {
    const allItems = data?.records || [];
    const filtered = searchQuery.trim()
      ? allItems.filter((item: ContextItem) => {
          const name = (item.name || '').toLowerCase();
          const description = (item.description || '').toLowerCase();
          const query = searchQuery.toLowerCase();
          return name.includes(query) || description.includes(query);
        })
      : allItems;
    return filtered.slice().sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [data?.records, searchQuery]);

  const totalPages = data?.total_pages || 1;

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setIsCreating(true);
    try {
      await contextService.create({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        dimension_filters: [],
      });
      queryClient.invalidateQueries({ queryKey: ['contextitems'] });
      setNewDialogOpen(false);
      setNewName('');
      setNewDescription('');
    } catch (error) {
      console.error('Failed to create context item:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenEdit = useCallback((item: ContextItem) => {
    setEditingItem(item);
    setEditName(item.name);
    setEditDescription(item.description || '');
    setEditFilters(item.dimension_filters || []);
    setEditSheetOpen(true);
  }, []);

  const handleSaveEdit = async () => {
    if (!editingItem || !editName.trim()) return;
    setIsSaving(true);
    try {
      await contextService.save({
        id: editingItem.id,
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        dimension_filters: editFilters,
      });
      queryClient.invalidateQueries({ queryKey: ['contextitems'] });
      setEditSheetOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to save context item:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDelete = useCallback((item: ContextItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await contextService.delete(itemToDelete.id);
      queryClient.invalidateQueries({ queryKey: ['contextitems'] });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Failed to delete context item:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const dimensionsById = useMemo(() => {
    const map: Record<string, string> = {};
    if (dimensionsData?.records) {
      for (const dim of dimensionsData.records) {
        map[dim.id] = dim.name;
      }
    }
    return map;
  }, [dimensionsData?.records]);

  const getFilterSummary = (filters: AnalysisDimensionFilter[]) => {
    if (!filters || filters.length === 0) return 'No filters';
    if (filters.length === 1) {
      const dimName = dimensionsById[filters[0].dimension_id] || 'Unknown';
      return `${dimName}`;
    }
    return `${filters.length} filters`;
  };

  const getInvalidDimensionIds = (filters: AnalysisDimensionFilter[]): string[] => {
    if (!filters || filters.length === 0) return [];
    return filters
      .filter((f) => f.dimension_id && !dimensionsById[f.dimension_id])
      .map((f) => f.dimension_id);
  };

  return (
    <>
      {/* Search + New */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search context..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={() => setNewDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Context
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading context items...</p>
          </div>
        </div>
      ) : error ? (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">Failed to load context items. Please try again.</p>
        </div>
      ) : contextItems.length === 0 ? (
        <div className="p-8 text-center">
          <Lightbulb className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'No context items match your search.' : 'No context items yet.'}
          </p>
          {!searchQuery && (
            <Button onClick={() => setNewDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Create your first context item
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[35%]">Name</TableHead>
                  <TableHead className="w-[35%]">Description</TableHead>
                  <TableHead className="w-[15%]">Filters</TableHead>
                  <TableHead className="w-[15%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contextItems.map((item: ContextItem) => {
                  const invalidDimensions = getInvalidDimensionIds(item.dimension_filters);
                  const hasInvalidDimensions = invalidDimensions.length > 0;
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 w-4 h-4 mt-0.5">
                            <Lightbulb className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <span>{item.name}</span>
                          {hasInvalidDimensions && (
                            <span title={`References ${invalidDimensions.length} dimension${invalidDimensions.length > 1 ? 's' : ''} that no longer exist`}>
                              <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <span className="line-clamp-2">{item.description || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Filter className="h-3 w-3" />
                          <span>{getFilterSummary(item.dimension_filters)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="outline" size="icon" onClick={() => handleOpenEdit(item)} title="Edit">
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="icon" onClick={() => handleOpenDelete(item)} title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => { e.preventDefault(); if (page > 1) setPage(page - 1); }}
                      style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                    if (pageNum === 1 || pageNum === totalPages || (pageNum >= page - 1 && pageNum <= page + 1)) {
                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink href="#" onClick={(e) => { e.preventDefault(); setPage(pageNum); }} isActive={pageNum === page}>
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    } else if (pageNum === page - 2 || pageNum === page + 2) {
                      return <PaginationItem key={`ellipsis-${pageNum}`}><PaginationEllipsis /></PaginationItem>;
                    }
                    return null;
                  })}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => { e.preventDefault(); if (page < totalPages) setPage(page + 1); }}
                      style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* New Context Dialog */}
      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Context Item</DialogTitle>
            <DialogDescription>Create a new context item. You can add dimension filters after creation.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="new-context-name">Name</Label>
              <Input
                id="new-context-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter context name"
                onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) handleCreate(); }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-context-description">Description</Label>
              <Textarea
                id="new-context-description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Enter context description (optional)"
                rows={3}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newName.trim() || isCreating}>
              {isCreating ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>) : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Context Dialog */}
      <Dialog open={editSheetOpen} onOpenChange={setEditSheetOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Context Item</DialogTitle>
            <DialogDescription>Update the context item details and dimension filters.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-6 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-context-name">Name</Label>
              <Input id="edit-context-name" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Enter context name" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-context-description">Description</Label>
              <Textarea id="edit-context-description" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Enter context description (optional)" rows={3} />
            </div>
            <div className="grid gap-2">
              <Label>Dimension Filters</Label>
              <p className="text-sm text-muted-foreground mb-2">This context will be shown when an analysis matches these dimension filters.</p>
              <DimensionFilterTable dimensionFilters={editFilters} onChange={setEditFilters} dimensionOptions={dimensionOptions} />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={!editName.trim() || isSaving}>
              {isSaving ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>) : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Context Item</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>) : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Company Context (unstructured) ─────────────────────────────────────────

function CompanyContextView() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 50;

  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newKeywords, setNewKeywords] = useState('');
  const [newBody, setNewBody] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<UnstructuredContextItem | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editKeywords, setEditKeywords] = useState('');
  const [editBody, setEditBody] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<UnstructuredContextItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, isLoading, error } = useUnstructuredContextList({ page, pageSize });

  const items = useMemo(() => {
    const allItems = data?.records || [];
    if (!searchQuery.trim()) return allItems;
    const query = searchQuery.toLowerCase();
    return allItems.filter((item: UnstructuredContextItem) => {
      const title = (item.title || '').toLowerCase();
      const keywords = (item.keywords || []).join(' ').toLowerCase();
      return title.includes(query) || keywords.includes(query);
    });
  }, [data?.records, searchQuery]);

  const totalPages = data?.total_pages || 1;

  const parseKeywords = (input: string): string[] =>
    input.split(',').map(k => k.trim()).filter(Boolean);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setIsCreating(true);
    try {
      await unstructuredContextService.create({
        title: newTitle.trim(),
        keywords: parseKeywords(newKeywords),
        body: newBody,
      });
      queryClient.invalidateQueries({ queryKey: ['unstructuredcontexts'] });
      setNewDialogOpen(false);
      setNewTitle('');
      setNewKeywords('');
      setNewBody('');
    } catch (error) {
      console.error('Failed to create document:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenEdit = useCallback((item: UnstructuredContextItem) => {
    setEditingItem(item);
    setEditTitle(item.title);
    setEditKeywords((item.keywords || []).join(', '));
    setEditBody(item.body || '');
    setEditDialogOpen(true);
  }, []);

  const handleSaveEdit = async () => {
    if (!editingItem || !editTitle.trim()) return;
    setIsSaving(true);
    try {
      await unstructuredContextService.save({
        id: editingItem.id,
        title: editTitle.trim(),
        keywords: parseKeywords(editKeywords),
        body: editBody,
      });
      queryClient.invalidateQueries({ queryKey: ['unstructuredcontexts'] });
      setEditDialogOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to save document:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDelete = useCallback((item: UnstructuredContextItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await unstructuredContextService.delete(itemToDelete.id);
      queryClient.invalidateQueries({ queryKey: ['unstructuredcontexts'] });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Failed to delete document:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <>
      {/* Search + New */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={() => setNewDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Document
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading documents...</p>
          </div>
        </div>
      ) : error ? (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">Failed to load documents. Please try again.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center">
          <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'No documents match your search.' : 'No documents yet.'}
          </p>
          {!searchQuery && (
            <Button onClick={() => setNewDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Create your first document
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30%]">Title</TableHead>
                  <TableHead className="w-[35%]">Keywords</TableHead>
                  <TableHead className="w-[15%]">Created</TableHead>
                  <TableHead className="w-[20%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item: UnstructuredContextItem) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-start gap-2">
                        <FileText className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                        <span>{item.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(item.keywords || []).map((kw, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{kw}</Badge>
                        ))}
                        {(!item.keywords || item.keywords.length === 0) && (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatDate(item.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="icon" onClick={() => handleOpenEdit(item)} title="Edit">
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => handleOpenDelete(item)} title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => { e.preventDefault(); if (page > 1) setPage(page - 1); }}
                      style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                    if (pageNum === 1 || pageNum === totalPages || (pageNum >= page - 1 && pageNum <= page + 1)) {
                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink href="#" onClick={(e) => { e.preventDefault(); setPage(pageNum); }} isActive={pageNum === page}>
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    } else if (pageNum === page - 2 || pageNum === page + 2) {
                      return <PaginationItem key={`ellipsis-${pageNum}`}><PaginationEllipsis /></PaginationItem>;
                    }
                    return null;
                  })}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => { e.preventDefault(); if (page < totalPages) setPage(page + 1); }}
                      style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Document</DialogTitle>
            <DialogDescription>Add a new context document for Ellie to reference.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="new-doc-title">Title</Label>
              <Input id="new-doc-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Enter document title" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-doc-keywords">Keywords</Label>
              <Input id="new-doc-keywords" value={newKeywords} onChange={(e) => setNewKeywords(e.target.value)} placeholder="Enter keywords, separated by commas" />
              <p className="text-xs text-muted-foreground">Comma-separated keywords that Ellie can search by.</p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-doc-body">Body</Label>
              <Textarea id="new-doc-body" value={newBody} onChange={(e) => setNewBody(e.target.value)} placeholder="Paste transcript, notes, or any reference material..." rows={10} />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newTitle.trim() || isCreating}>
              {isCreating ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>) : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>Update the document details.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-doc-title">Title</Label>
              <Input id="edit-doc-title" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Enter document title" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-doc-keywords">Keywords</Label>
              <Input id="edit-doc-keywords" value={editKeywords} onChange={(e) => setEditKeywords(e.target.value)} placeholder="Enter keywords, separated by commas" />
              <p className="text-xs text-muted-foreground">Comma-separated keywords that Ellie can search by.</p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-doc-body">Body</Label>
              <Textarea id="edit-doc-body" value={editBody} onChange={(e) => setEditBody(e.target.value)} placeholder="Paste transcript, notes, or any reference material..." rows={10} />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={!editTitle.trim() || isSaving}>
              {isSaving ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>) : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>) : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Main Context Page with Toggle ──────────────────────────────────────────

export default function Context() {
  const [viewMode, setViewMode] = useState<ContextViewMode>('analytics');

  const subtitle = 'Manage analytics context surfaced by dimension filters and company documents that Ellie can search.';

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh - 48px)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <PageTitle title="Context" titleClassName="text-xl font-semibold text-foreground" />
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <ContextViewToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {viewMode === 'analytics' ? <AnalyticsContextView /> : <CompanyContextView />}
      </div>
    </div>
  );
}
